import { cn } from "@/lib/utils";
import { ComponentWithDefaultProps, TODO } from "@/types/utility";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { LatLngBounds, LatLngTuple } from "leaflet";

import { ReactNode, useEffect } from "react";
import { useMap } from "react-leaflet";
import { ApplicationPage, ApplicationPageChildren, ApplicationPageHeading, ApplicationPageSubHeading } from "./layout/application.page";
import { EsriBaseMapLayer } from "./map/map-content";
import { MapWrapper } from "./map/map-wrapper";
import { AppSearch } from "./routes/app-route.zod"
import { helpRoute } from "./routes/help-route";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, StyledDialogClose } from "./ui/containers/dialog";
import { Icons } from "./ui/icons";
import { Link } from "./ui/primitives/link";
import { Separator } from "./ui/primitives/separator";
import { focusRing } from "./ui/shared/component-state";
import { ScrollAreaNative } from "./ui/containers/scroll-area.native";

const pages = [
  {
    title: "Getting Started",
    url: "/help",
    icon: <Icons.general.flag className="size-4" />,
  },
  {
    title: "How-To",
    url: "/help/how-to",
    icon: <Icons.general.book className="size-4" />,
  },
  {
    title: "Feedback",
    url: "/help/feedback",
    icon: <Icons.general.sticker className="size-4" />,
  },
] satisfies { title: string; url: string; icon: ReactNode }[]

const southWestMichigan = [
  43.292915090000065, -86.54113973899996,
] as const satisfies LatLngTuple
const northEastMichigan = [
  45.810152310000035, -83.25995309399997,
] as const satisfies LatLngTuple
/**
 * Represents the bounding box containing all of the available regions
 * of Michigan (matched expected OTP Regions bounding box).
 */
const MICHIGAN_BBOX = new LatLngBounds(southWestMichigan, northEastMichigan)

export function HelpPage({ children }: { children?: React.ReactNode }) {
  const search = helpRoute.useSearch() as AppSearch
  const navigate = useNavigate()

  return (
    <>
      <div
        className={
          "relative group map-container flex-1 grid overflow-hidden shadow-card"
        }
      >
        <MapWrapper>
          <EsriBaseMapLayer />
          <Sync />
        </MapWrapper>
      </div>
      <Dialog
        open
        onOpenChange={() => {
          navigate({
            to: "/",
            search: search,
          })
        }}
      >
        <DialogContent
          className="flex flex-col max-h-[80svh] md:max-h-[75svh] max-w-[clamp(24rem,_calc(100svw-2rem),_48rem)] lg:max-w-7xl"
          close={
            <StyledDialogClose>
              <Link
                to={'/'}
                // @ts-expect-error search type mis match with new tanstack properties
                search={search}
                variant={"ghost"}
                className="flex flex-row gap-2 justify-center items-center text-base"
              >
                Start Planning{" "}
                <p>
                  <Icons.actions.close className="w-4 h-4" />
                </p>
              </Link>
            </StyledDialogClose>
          }
          aria-describedby="help-page-content"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">Help</DialogTitle>
            <DialogDescription id="help-page-content" className="sr-only">
              Help page content.
            </DialogDescription>
          </DialogHeader>
          <Layout search={search}>{children}</Layout>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function Sync() {
  const map = useMap()

  useEffect(() => {
    if (!map) {
      return
    }

    map.fitBounds(MICHIGAN_BBOX, { animate: true, padding: [25, 25] })
  }, [map])

  return undefined
}

function Layout({
  children,
  search,
}: Readonly<{
  children: React.ReactNode
  search: TODO
}>) {
  return (
    <ApplicationPage
      className="items-start h-full mb-0 sm:mb-0 sm:mt-4 space-y-0 sm:space-y-0 overflow-y-hidden"
      heading={
        <ApplicationPageHeading
          className="space-y-2 top-8 bg-popover/80 backdrop-blur-md pb-2 shadow-lg shadow-white dark:shadow-none"
          subHeading={
            <ApplicationPageSubHeading
              className="max-w-6xl mt-0 space-y-8 line-clamp-none"
              as="div"
            >
              <p>
                Created to improve travel in rural communities, this tool brings transit information together so you can explore your options and plan your ride with confidence.
                Find helpful tips and guidance to make planning your trip simple and stress-free.
              </p>
              <div className="overflow-x-auto flex flex-row gap-2">
                {pages.map((page) => (
                  <LayoutLink
                    key={page.url}
                    href={page.url}
                    search={search}
                    className={cn(
                      focusRing,
                      "relative inline-flex items-center justify-between whitespace-nowrap rounded-md border text-center font-medium transition-all duration-100 ease-in-out",
                      "disabled:pointer-events-none disabled:shadow-none disabled:opacity-50",
                      "group",
                      "bg-transparent",
                      "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 shadow-none",
                      "text-muted-foreground" 
                    )}
                    activeOptions={{
                      exact: true,
                    }}
                    activeProps={{
                      className:
                        "bg-accent border border-border/25 text-muted-foreground",
                    }}
                  >
                    {page.icon}
                    {page.title}
                  </LayoutLink>
                ))}
              </div>
            </ApplicationPageSubHeading>
          }
        >
          Welcome to the Michigan Trip Planner!
        </ApplicationPageHeading>
      }
    >
      <ApplicationPageChildren className="flex-1 h-full overflow-y-hidden m-0">
        <ScrollAreaNative variant="article" className="container p-0 flex flex-col text-foreground mt-8 px-2 md:px-8 pb-8">
          {children}
          <Separator className="bg-popover my-4" />
        </ScrollAreaNative>
      </ApplicationPageChildren>
    </ApplicationPage>
  )
}

function LayoutLink({
  className,
  children,
  href,
  activeProps,
  activeOptions,
  ...rest
}: ComponentWithDefaultProps<
  React.ComponentRef<typeof Link>,
  React.ComponentProps<typeof Link> & {
    activeOptions: { exact: boolean }
    activeProps: { className: string }
  }
>) {
  const path = useLocation({ select: (location) => location.pathname })
  const isActive = activeOptions.exact
    ? path === href
    : !!href && path.includes(href.toString())

  return (
    <Link
      className={cn(
        "flex items-center justify-start gap-2",
        className,
        isActive ? activeProps.className : ""
      )}
      to={href}
      replace
      {...rest}
    >
      {children}
    </Link>
  )
}
